import {
  BooleanFilterTypes,
  CurrencyFilterTypes,
  FilterOperatorTypes,
  TxnFilterQuery,
} from '../../graphql/types';
import { ROUTES } from '../../routes';
import {
  buildBooleanFilterObject,
  buildCredentialIdsFilterObject,
  buildCurrencyFilterObject,
  buildDateFilterObject,
  buildFromAddressFilterObject,
  buildMethodIdFilterObject,
  buildReconIdentifierFilterObject,
  buildToAddressFilterObject,
  buildTransferTopicFingerprintFilterObject,
  buildTxnHashFilterObject,
  buildTxnTopicFingerprintFilterObject,
} from './legacyFilterBuilders';
import type { IntentAdapter } from './intents';
import { filterUrl, ok, unsupported, withSort } from './serialize';

const missingPriceTxnTypes = [
  'airdrop',
  'borrow',
  'fork',
  'income',
  'migration',
  'mining',
  'staking',
  'trade',
];

function withSpecId(query: TxnFilterQuery, specIdSellId?: string | null): TxnFilterQuery {
  if (!specIdSellId) return query;
  return { ...query, specId: { sellId: specIdSellId } };
}

function legacyUrl(query: TxnFilterQuery, specIdSellId?: string | null, sort?: 'oldestFirst'): string {
  return withSort(filterUrl(ROUTES.allTransactions, withSpecId(query, specIdSellId)), 'legacy', sort);
}

export const legacyAdapter: IntentAdapter = {
  all: ({ sort }) => ok(withSort(ROUTES.allTransactions, 'legacy', sort)),
  byHash: ({ hashes, specIdSellId }) =>
    ok(legacyUrl({ txnHash: hashes.map(buildTxnHashFilterObject) }, specIdSellId)),
  byImports: ({ credentialIds, specIdSellId, sort }) =>
    ok(legacyUrl({ imports: buildCredentialIdsFilterObject(credentialIds) }, specIdSellId, sort)),
  byAccounts: ({ accountIds }) =>
    ok(
      legacyUrl({
        accounts: { operator: FilterOperatorTypes.Is, values: accountIds },
      }),
    ),
  byCurrency: ({ currency, sort }) =>
    ok(
      legacyUrl(
        { currency: [buildCurrencyFilterObject(currency, CurrencyFilterTypes.Currency)] },
        null,
        sort,
      ),
    ),
  byCurrencyOnDate: ({ date, currency }) =>
    ok(
      legacyUrl({
        date: buildDateFilterObject(date),
        ...(currency
          ? { currency: [buildCurrencyFilterObject(currency, CurrencyFilterTypes.Currency)] }
          : {}),
      }),
    ),
  byDateCurrencyHash: ({ date, currency, hash }) => {
    const query: TxnFilterQuery = { date: { fromDate: date, toDate: date } };
    if (currency) {
      query.currency = [
        {
          operator: FilterOperatorTypes.Is,
          side: CurrencyFilterTypes.Currency,
          symbols: [currency.toLowerCase()],
        },
      ];
    }
    if (hash) {
      query.txnHash = [buildTxnHashFilterObject(hash)];
    }
    return ok(legacyUrl(query));
  },
  byMethodId: ({ methodId, specIdSellId }) =>
    ok(legacyUrl({ methodId: [buildMethodIdFilterObject(methodId)] }, specIdSellId)),
  byContractAddress: ({ address, specIdSellId }) =>
    ok(legacyUrl({ toAddress: [buildToAddressFilterObject(address)] }, specIdSellId)),
  byTransferAddress: ({ address, direction, specIdSellId }) =>
    ok(
      legacyUrl(
        direction === 'out'
          ? { fromAddress: [buildFromAddressFilterObject(address)] }
          : { toAddress: [buildToAddressFilterObject(address)] },
        specIdSellId,
      ),
    ),
  byTransferGroup: () => unsupported('transfer group ids are not modeled for legacy transactions'),
  workpaperTransactions: ({
    credentialIds,
    reconIdentifier,
    contractAddress,
    methodId,
    manualImportsOnly,
    fromDate,
    toDate,
  }) => {
    const query: TxnFilterQuery = {};
    if (credentialIds?.length) {
      query.imports = buildCredentialIdsFilterObject(credentialIds);
    }
    if (reconIdentifier) {
      query.reconIdentifier = [buildReconIdentifierFilterObject(reconIdentifier)];
    } else if (contractAddress) {
      query.toAddress = [buildToAddressFilterObject(contractAddress)];
      if (methodId) query.methodId = [buildMethodIdFilterObject(methodId)];
    } else if (manualImportsOnly) {
      query.importType = { operator: FilterOperatorTypes.Is, values: ['form'] };
    }
    if (fromDate || toDate) {
      query.date = { ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}) };
    }
    return ok(legacyUrl(query, null, 'oldestFirst'));
  },
  pricingAnomalies: () =>
    ok(legacyUrl({ boolean: [buildBooleanFilterObject(BooleanFilterTypes.isPricingAnomaly, true)] })),
  missingPrices: ({ fromDate }) =>
    ok(
      legacyUrl(
        {
          boolean: [buildBooleanFilterObject(BooleanFilterTypes.missingPrice, true)],
          txnType: { operator: FilterOperatorTypes.Is, values: [...missingPriceTxnTypes] },
          ...(fromDate ? { date: { fromDate } } : {}),
        },
        null,
        'oldestFirst',
      ),
    ),
  byReconIdentifier: ({ reconIdentifier, specIdSellId }) =>
    ok(legacyUrl({ reconIdentifier: [buildReconIdentifierFilterObject(reconIdentifier)] }, specIdSellId)),
  byTxnTopicFingerprint: ({ fingerprint, specIdSellId }) =>
    ok(legacyUrl({ txnTopicFingerprint: [buildTxnTopicFingerprintFilterObject(fingerprint)] }, specIdSellId)),
  byTransferTopicFingerprint: ({ fingerprint, specIdSellId }) =>
    ok(
      legacyUrl(
        { transferTopicFingerprint: [buildTransferTopicFingerprintFilterObject(fingerprint)] },
        specIdSellId,
      ),
    ),
};
